import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCommDispatcher, useIntercomDispatcher } from '@/hooks/comm/use-comm';
import { friendsQueryOptions } from '@/hooks/contacts/use-friends';
import { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import { tagListQueryOptions } from '@/hooks/tags/use-tags';
import { generateFriendInfoQueryOptions } from '@/hooks/users/use-friend-info';
import type { RespondedType, ResponseError } from '@/models/response';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';

export default function useDeleteFriend() {
  const queryClient = useQueryClient();
  const { dispatch } = useCommDispatcher();
  const { dispatch: interDispatch } = useIntercomDispatcher();

  return useMutation<RespondedType, ResponseError, number>({
    mutationKey: ['/DeleteFriend'],
    mutationFn: (userId) => fetcher('/DeleteFriend', { UserID: userId }, { validResponseCodes: [0] }),
    onSuccess: async (_, userId) => {
      if (!userId) return;

      useConversationSessionStore.getState().removeSessionByUserId(userId);
      dispatch({
        event: 'leave-session',
        data: { type: 'user', id: userId },
      });
      interDispatch({
        event: 'leave-session',
        data: { type: 'user', id: userId },
      });

      await Promise.allSettled([
        queryClient.invalidateQueries(friendsQueryOptions),
        queryClient.invalidateQueries(allChatSessionQueryOptions),
        queryClient.invalidateQueries(generateFriendInfoQueryOptions(userId)),
        queryClient.invalidateQueries(tagListQueryOptions),
        queryClient.invalidateQueries({ queryKey: ['/GetTagMembers'] }),
      ]);
    },
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
  });
}
